import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { FileType } from '../models/file-item.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { ContextService } from '../shared-context/context.service';
import { TemplatesConfigService } from '../templates/templates-config.service';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { ODSPConfigService } from './odsp-config.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { WorkloadsService } from './workloads.service';
import { XhrService } from './xhr.service';

export interface ODSPProvisioningStatus {
    SharepointUrl: string;
    OneDriveUrl: string;
}

export interface VisioLicenseStatus {
    App: string;
    IsLicensed: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ShellService extends XhrService {
    private readonly requestConfig: HttpRequestOptions = {
        headers: new Map([
            ['Accept', 'application/json']
        ])
    };

    private getOdspProvisioningStatusCache: Promise<ODSPProvisioningStatus>;
    private getLicenseStatusCache: Promise<VisioLicenseStatus>;

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        scheduler: SchedulerService,
        private readonly odspConfigService: ODSPConfigService,
        private readonly templatesConfigService: TemplatesConfigService,
        private readonly workloadsService: WorkloadsService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.OfficeHome, contextService, officeHomeApiConfigService, platformLocation, scheduler);
    }

    public getOdspProvisioningStatus(priority?: SchedulePriority, timeout?: number): Promise<ODSPProvisioningStatus> {
        if (this.getOdspProvisioningStatusCache) {
            return this.getOdspProvisioningStatusCache;
        }
        const pr: Promise<ODSPProvisioningStatus> = new Promise((resolve, reject) => {
            this.get<ODSPProvisioningStatus>(KnownInstrumentationEvents.ODSPProvisioningStatus, KnownRequestInstrumentationEvents.ODSPProvisioningStatus, this.contextService.sharedContext.officeHomeApiRoot + this.odspConfigService.config.odspProvisioningStatusApiPath, (promise: InstrumentedHttpPromise<ODSPProvisioningStatus>) => {
                promise.then(response => {
                    if (!response.data.SharepointUrl && !response.data.OneDriveUrl) {
                        this.getOdspProvisioningStatusCache = null;
                    }
                    resolve(response.data);
                });
            }, error => {
                this.getOdspProvisioningStatusCache = null;
                reject(error);
            },
            this.requestConfig,
            FetchStrategy.NetworkOnly, undefined, undefined, timeout, undefined, undefined, priority);
        });
        this.getOdspProvisioningStatusCache = pr;
        return pr;
    }

    public getLicenseStatus(app: string, scenarioId: string, priority?: SchedulePriority, timeout?: number): Promise<VisioLicenseStatus> {
        if (this.getLicenseStatusCache) {
            return this.getLicenseStatusCache;
        }
        const pr: Promise<VisioLicenseStatus> = new Promise((resolve, reject) => {
            const visioTemplatesResources = this.templatesConfigService.resources(FileType.Visio);
            this.get<VisioLicenseStatus>(KnownInstrumentationEvents.VisioLicenseStatus,
                KnownRequestInstrumentationEvents.VisioLicenseStatus,
                this.contextService.sharedContext.officeHomeApiRoot + visioTemplatesResources.LicenseStatusApiPath + `?app=${app}&scenarioId=${scenarioId}`,
                (promise: InstrumentedHttpPromise<VisioLicenseStatus>) => {
                promise.then(response => {
                    if (!response.data.App && !response.data.IsLicensed) {
                        this.getLicenseStatusCache = null;
                    }
                    resolve(response.data);
                });
            }, error => {
                this.getLicenseStatusCache = null;
                reject(error);
            },
            this.requestConfig,
            FetchStrategy.NetworkOnly, undefined, undefined, timeout, undefined, undefined, priority);
        });
        this.getLicenseStatusCache = pr;
        return pr;
    }

    public checkInAppNavEnabled(fileType ?: FileType): Promise<boolean> {
        return new Promise(resolve => {
            this.workloadsService.getWorkloads().then(workloads => {
                if (fileType) {
                    const selectedCheck = workloads.find(w => w.FileType === fileType);
                    resolve(selectedCheck && selectedCheck.InAppNav);
                } else {
                    const sharepoint = workloads.find(w => w.FileType === FileType.SharePoint);
                    const onedrive = workloads.find(w => w.FileType === FileType.OneDrive);
                    resolve((sharepoint && sharepoint.InAppNav) || (onedrive && onedrive.InAppNav));
                }
            });
        });
    }

    public initializeProvisioningStatus() {
        this.checkInAppNavEnabled().then( enabled => {
            if (enabled) {
                this.getOdspProvisioningStatus();
            }
        });
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>) {
        if (paramNameInLowerCase === 'mockshellservicemode' && this.mockRequestHeader) {
            this.requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }
}
